import { Component } from '@opten/compiler/metadata/component';
import { debounce } from '@opten/reactive/debounce';
import { PageRenderHelper } from './page-render-helper';

@Component({ className: 'js-media-transition' })
export class MediaTransitionComponent {
	constructor(private element: HTMLElement) {
		const observer = new IntersectionObserver(function (entries, observer) {
			entries.forEach(function (entry) {
				if (entry.isIntersecting) {
					sketch.play();
				} else {
					sketch.stop();
				}
			}, {});
		});

		const duration = parseInt(
			element.getAttribute('data-duration') || '3000'
		);
		let sketch = new Sketch(element, {
			intervalDuration: duration,
			uniforms: {
				intensity: { value: 0.3, type: 'f', min: 0, max: 2 }
			},
			fragment: `
				uniform float time;
				uniform float progress;
				uniform float width;
				uniform float scaleX;
				uniform float scaleY;
				uniform float transition;
				uniform float radius;
				uniform float intensity;
				uniform sampler2D texture1;
				uniform sampler2D texture2;
				uniform sampler2D displacement;
				uniform vec4 resolution;
				varying vec2 vUv;
				void main()	{
				  vec2 newUV = (vUv - vec2(0.5))*resolution.zw + vec2(0.5);
				 vec4 d1 = texture2D(texture1, newUV);
				 vec4 d2 = texture2D(texture2, newUV);
				 float displace1 = (d1.r + d1.g + d1.b)*0.33;
				 float displace2 = (d2.r + d2.g + d2.b)*0.33;
				 
				 vec4 t1 = texture2D(texture1, vec2(newUV.x, newUV.y + progress * (displace2 * intensity)));
				 vec4 t2 = texture2D(texture2, vec2(newUV.x, newUV.y + (1.0 - progress) * (displace1 * intensity)));
				 gl_FragColor = mix(t1, t2, progress);
				}
			`
		});
		sketch.init().then(() => {
			observer.observe(this.element);
		});
		PageRenderHelper.onPageChange.subscribe(() => sketch.stop());
	}
}

declare const THREE: any;
declare const TimelineMax: any;
declare const Power2: any;
class Sketch {
	private scene: any;
	private vertex: string;
	private fragment: any;
	private uniforms: any;
	private renderer: any;
	private width: number;
	private height: number;
	private duration: any;
	private easing: any;
	// private clicker: HTMLElement;
	private images: any;
	private camera: any;
	private time: number;
	private current: number;
	private textures: any[];
	private paused: boolean;
	private imageAspect: number;
	private material: any;
	private plane: any;
	private geometry: any;
	private isRunning: any;
	private settings: any;
	private intervalDuration: any;
	private interval: any;

	constructor(private container: HTMLElement, opts: any) {
		this.scene = new THREE.Scene();
		this.vertex = `varying vec2 vUv;void main() {vUv = uv;gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );}`;
		this.intervalDuration = opts.intervalDuration;
		this.fragment = opts.fragment;
		this.uniforms = opts.uniforms;
		this.renderer = new THREE.WebGLRenderer();
		this.width = this.container.offsetWidth;
		this.height = this.container.offsetHeight;
		this.renderer.setPixelRatio(window.devicePixelRatio);
		this.renderer.setSize(this.width, this.height);
		this.renderer.setClearColor(0xeeeeee, 1);
		this.duration = opts.duration || 1;
		this.easing = opts.easing || 'easeInOut';

		this.images = JSON.parse(this.container.getAttribute('data-images'));
		this.container.appendChild(this.renderer.domElement);

		this.camera = new THREE.PerspectiveCamera(
			70,
			window.innerWidth / window.innerHeight,
			0.001,
			1000
		);

		this.camera.position.set(0, 0, 2);
		this.time = 0;
		this.current = 0;
		this.textures = [];

		this.paused = true;
	}

	init() {
		return new Promise<void>(resolve => {
			this.initiate(() => {
				this.setupResize();
				this.setSettings();
				this.addObjects();
				this.resize();
				this.clickEvent();
				this.play();
				resolve();
			});
		});
	}

	private initiate(cb) {
		const promises = [];
		this.images.forEach((url, i) => {
			let promise = new Promise(resolve => {
				this.textures[i] = new THREE.TextureLoader().load(url, resolve);
			});
			promises.push(promise);
		});

		Promise.all(promises).then(() => {
			cb();
		});
	}

	clickEvent() {
		this.container.addEventListener('click', () => {
			this.next();
		});
	}
	setSettings() {
		this.settings = { progress: 0.5 };
		Object.keys(this.uniforms).forEach(item => {
			this.settings[item] = this.uniforms[item].value;
		});
	}

	setupResize() {
		new ResizeObserver(debounce(() => this.resize(), 10)).observe(
			this.container
		);
	}

	resize() {
		this.width = this.container.offsetWidth;
		this.height = this.container.offsetHeight;
		this.renderer.setSize(this.width, this.height);
		this.camera.aspect = this.width / this.height;

		// image cover
		this.imageAspect =
			this.textures[0].image.height / this.textures[0].image.width;
		let a1;
		let a2;
		if (this.height / this.width > this.imageAspect) {
			a1 = (this.width / this.height) * this.imageAspect;
			a2 = 1;
		} else {
			a1 = 1;
			a2 = this.height / this.width / this.imageAspect;
		}

		this.material.uniforms.resolution.value.x = this.width;
		this.material.uniforms.resolution.value.y = this.height;
		this.material.uniforms.resolution.value.z = a1;
		this.material.uniforms.resolution.value.w = a2;

		const dist = this.camera.position.z;
		const height = 1;
		this.camera.fov = 2 * (180 / Math.PI) * Math.atan(height / (2 * dist));

		this.plane.scale.x = this.camera.aspect;
		this.plane.scale.y = 1;

		this.camera.updateProjectionMatrix();
	}

	addObjects() {
		this.material = new THREE.ShaderMaterial({
			extensions: {
				derivatives: '#extension GL_OES_standard_derivatives : enable'
			},
			side: THREE.DoubleSide,
			uniforms: {
				time: { type: 'f', value: 0 },
				progress: { type: 'f', value: 0 },
				border: { type: 'f', value: 0 },
				intensity: { type: 'f', value: 0 },
				scaleX: { type: 'f', value: 40 },
				scaleY: { type: 'f', value: 40 },
				transition: { type: 'f', value: 40 },
				swipe: { type: 'f', value: 0 },
				width: { type: 'f', value: 0 },
				radius: { type: 'f', value: 0 },
				texture1: { type: 'f', value: this.textures[0] },
				texture2: { type: 'f', value: this.textures[1] },
				displacement: {
					type: 'f'
					//value: new THREE.TextureLoader().load('img/disp1.jpg') // https://tympanus.net/Development/webGLImageTransitions/img/disp1.jpg
				},
				resolution: { type: 'v4', value: new THREE.Vector4() }
			},
			// wireframe: true,
			vertexShader: this.vertex,
			fragmentShader: this.fragment
		});

		this.geometry = new THREE.PlaneGeometry(1, 1, 2, 2);

		this.plane = new THREE.Mesh(this.geometry, this.material);
		this.scene.add(this.plane);
	}

	stop() {
		this.paused = true;
		clearInterval(this.interval);
		this.renderer.info.reset();
		this.renderer.dispose();
	}

	play() {
		this.paused = false;
		this.render();
		clearInterval(this.interval);
		this.interval = setInterval(() => {
			this.next();
		}, this.intervalDuration);
	}

	next() {
		if (this.isRunning) return;
		this.isRunning = true;
		clearInterval(this.interval);
		this.interval = setInterval(() => {
			this.next();
		}, this.intervalDuration);
		let len = this.textures.length;
		let nextTexture = this.textures[(this.current + 1) % len];
		this.material.uniforms.texture2.value = nextTexture;
		let tl = new TimelineMax();
		tl.to(this.material.uniforms.progress, this.duration, {
			value: 1,
			ease: Power2[this.easing],
			onComplete: () => {
				this.current = (this.current + 1) % len;
				this.material.uniforms.texture1.value = nextTexture;
				this.material.uniforms.progress.value = 0;
				this.isRunning = false;
			}
		});
	}
	render() {
		if (this.paused) return;
		this.time += 0.05;
		this.material.uniforms.time.value = this.time;
		// this.material.uniforms.progress.value = this.settings.progress;

		Object.keys(this.uniforms).forEach(item => {
			this.material.uniforms[item].value = this.settings[item];
		});

		// this.camera.position.z = 3;
		// this.plane.rotation.y = 0.4*Math.sin(this.time)
		// this.plane.rotation.x = 0.5*Math.sin(0.4*this.time)

		requestAnimationFrame(this.render.bind(this));
		this.renderer.render(this.scene, this.camera);
	}
}
